import React, { useState } from "react";
import api from "../../api/api";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  Alert,
} from "@mui/material";

import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleRequest = async (e) => {
    e.preventDefault();

    if (loading) return;

    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      setMessage("Please enter your registered email address.");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await api.post("/request-reset-code/", {
        email: normalizedEmail,
      });

      setMessage(
        res.data?.message ||
          "Verification code sent. Check your email."
      );
      setMessageType("success");

      navigate("/verifyCode", {
        state: { email: normalizedEmail },
      });
    } catch (err) {
      setMessage(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "We could not process your request. Please try again."
      );
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const styles = `
    .jana-forgot-page {
      --fp-purple: #54218f;
      --fp-deep: #35105f;
      --fp-gold: #f4c542;
      --fp-gold-dark: #d6a817;
      --fp-text: #29233a;
      --fp-muted: #777184;

      position: relative;
      isolation: isolate;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      min-height: 100dvh;
      padding: 35px 18px;
      overflow: hidden;
      background:
        radial-gradient(ellipse at 10% 10%, rgba(84, 33, 143, .10), transparent 34%),
        radial-gradient(ellipse at 90% 90%, rgba(244, 197, 66, .15), transparent 35%),
        linear-gradient(145deg, #ffffff 0%, #faf7ff 55%, #f6efff 100%);
      font-family: inherit;
    }

    .jana-forgot-page *,
    .jana-forgot-page *::before,
    .jana-forgot-page *::after {
      box-sizing: border-box;
    }

    .fp-background-shape {
      position: absolute;
      z-index: -1;
      border-radius: 50%;
      pointer-events: none;
      animation: fp-float 13s ease-in-out infinite;
    }

    .fp-shape-one {
      top: -125px;
      left: -100px;
      width: 350px;
      height: 350px;
      border: 55px solid rgba(84, 33, 143, .065);
    }

    .fp-shape-two {
      right: -135px;
      bottom: -100px;
      width: 400px;
      height: 400px;
      border: 65px solid rgba(244, 197, 66, .13);
      animation-delay: -5s;
    }

    .fp-shape-three {
      top: 20%;
      right: 13%;
      width: 95px;
      height: 95px;
      background: rgba(84, 33, 143, .055);
      animation-delay: -3s;
    }

    .fp-shape-four {
      bottom: 17%;
      left: 12%;
      width: 60px;
      height: 60px;
      background: rgba(244, 197, 66, .20);
      animation-delay: -7s;
    }

    @keyframes fp-float {
      0%, 100% {
        transform: translate3d(0, 0, 0);
      }
      50% {
        transform: translate3d(0, -20px, 0);
      }
    }

    .fp-layout {
      display: grid;
      grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
      width: 100%;
      max-width: 1030px;
      min-height: 590px;
      overflow: hidden;
      border: 1px solid rgba(84, 33, 143, .12);
      border-radius: 28px;
      background: rgba(255, 255, 255, .93);
      box-shadow:
        0 30px 90px rgba(53, 16, 95, .12),
        0 5px 18px rgba(53, 16, 95, .045);
      backdrop-filter: blur(18px);
    }

    .fp-brand-panel {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      min-width: 0;
      padding: 42px 35px;
      overflow: hidden;
      color: #ffffff;
      background:
        radial-gradient(circle at 100% 0%, rgba(244, 197, 66, .18), transparent 32%),
        linear-gradient(145deg, #35105f 0%, #54218f 62%, #6730a5 100%);
    }

    .fp-brand-panel::before {
      content: "";
      position: absolute;
      top: 40%;
      right: -115px;
      width: 270px;
      height: 270px;
      border: 1px solid rgba(255, 255, 255, .12);
      border-radius: 50%;
      box-shadow:
        0 0 0 28px rgba(255, 255, 255, .025),
        0 0 0 58px rgba(255, 255, 255, .025);
      pointer-events: none;
    }

    .fp-brand-top,
    .fp-brand-middle,
    .fp-brand-bottom {
      position: relative;
      z-index: 1;
    }

    .fp-brand-top {
      display: flex;
      align-items: center;
      gap: 13px;
    }

    .fp-brand-logo {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 49px;
      height: 49px;
      flex-shrink: 0;
      border: 1px solid rgba(244, 197, 66, .45);
      border-radius: 15px;
      background: rgba(244, 197, 66, .12);
      color: var(--fp-gold);
      font-size: 26px;
    }

    .fp-brand-name {
      margin: 0;
      color: #ffffff;
      font-size: 14px;
      font-weight: 850;
      line-height: 1.5;
      letter-spacing: .4px;
      overflow-wrap: anywhere;
    }

    .fp-brand-caption {
      margin: 4px 0 0;
      color: rgba(255, 255, 255, .67);
      font-size: 10px;
      font-weight: 750;
      letter-spacing: 1.5px;
      text-transform: uppercase;
    }

    .fp-brand-middle {
      margin: 65px 0;
    }

    .fp-brand-eyebrow {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 20px;
      color: var(--fp-gold);
      font-size: 10px;
      font-weight: 850;
      letter-spacing: 2px;
      text-transform: uppercase;
    }

    .fp-brand-eyebrow::before {
      content: "";
      width: 24px;
      height: 3px;
      border-radius: 5px;
      background: var(--fp-gold);
    }

    .fp-brand-heading {
      margin: 0 0 19px;
      color: #ffffff;
      font-size: clamp(32px, 4vw, 43px);
      font-weight: 850;
      line-height: 1.15;
      letter-spacing: -1.3px;
    }

    .fp-brand-heading span {
      color: var(--fp-gold);
    }

    .fp-brand-description {
      max-width: 340px;
      margin: 0;
      color: rgba(255, 255, 255, .76);
      font-size: 13px;
      line-height: 1.95;
    }

    .fp-brand-points {
      display: grid;
      gap: 15px;
      margin-top: 30px;
    }

    .fp-brand-point {
      display: flex;
      align-items: center;
      gap: 11px;
      color: rgba(255, 255, 255, .90);
      font-size: 12px;
      font-weight: 600;
    }

    .fp-brand-point-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      flex-shrink: 0;
      border-radius: 9px;
      background: rgba(244, 197, 66, .13);
      color: var(--fp-gold);
      font-size: 14px;
    }

    .fp-brand-bottom {
      padding-top: 20px;
      border-top: 1px solid rgba(255, 255, 255, .15);
      color: rgba(255, 255, 255, .58);
      font-size: 10px;
      line-height: 1.8;
    }

    .fp-form-panel {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 0;
      padding: 55px 48px;
      background: rgba(255, 255, 255, .96);
    }

    .fp-form-content {
      width: 100%;
      max-width: 390px;
    }

    .fp-form-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 68px;
      height: 68px;
      margin-bottom: 25px;
      border: 1px solid rgba(84, 33, 143, .12);
      border-radius: 21px;
      background: linear-gradient(145deg, #f6efff, #e9dcfa);
      color: var(--fp-purple);
      font-size: 33px;
      box-shadow: 0 8px 22px rgba(84, 33, 143, .09);
    }

    .fp-form-eyebrow {
      margin: 0 0 10px;
      color: #a27b18;
      font-size: 10px;
      font-weight: 850;
      letter-spacing: 1.8px;
      text-transform: uppercase;
    }

    .fp-form-title {
      margin: 0 0 13px;
      color: var(--fp-deep);
      font-size: clamp(29px, 3vw, 36px);
      font-weight: 850;
      line-height: 1.2;
      letter-spacing: -1px;
    }

    .fp-form-description {
      margin: 0;
      color: var(--fp-muted);
      font-size: 13px;
      line-height: 1.9;
    }

    .fp-email-field {
      margin-top: 30px !important;
    }

    .fp-email-field .MuiOutlinedInput-root {
      min-height: 57px;
      border-radius: 13px;
      background: #fcfaff;
      transition: box-shadow .2s ease;
    }

    .fp-email-field .MuiOutlinedInput-notchedOutline {
      border-color: #e7dff0;
    }

    .fp-email-field .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
      border-color: #ad8bd5;
    }

    .fp-email-field .MuiOutlinedInput-root.Mui-focused {
      box-shadow: 0 0 0 4px rgba(84, 33, 143, .08);
    }

    .fp-email-field .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-width: 1.5px;
      border-color: var(--fp-purple);
    }

    .fp-email-field .MuiInputLabel-root.Mui-focused {
      color: var(--fp-purple);
    }

    .fp-primary-button.MuiButton-root {
      min-height: 54px;
      border-radius: 13px;
      background: linear-gradient(110deg, #54218f, #6730a5);
      color: #ffffff;
      font-size: 12px;
      font-weight: 850;
      letter-spacing: .3px;
      text-transform: none;
      box-shadow: 0 9px 22px rgba(84, 33, 143, .20);
      transition:
        transform .2s ease,
        box-shadow .2s ease,
        background .2s ease;
    }

    .fp-primary-button.MuiButton-root:hover {
      transform: translateY(-2px);
      background: linear-gradient(110deg, #35105f, #54218f);
      box-shadow: 0 13px 27px rgba(84, 33, 143, .27);
    }

    .fp-primary-button.MuiButton-root.Mui-disabled {
      background: #b9a5d0;
      color: #ffffff;
    }

    .fp-back-button.MuiButton-root {
      min-height: 46px;
      margin-top: 10px;
      border-radius: 12px;
      color: var(--fp-purple);
      font-size: 12px;
      font-weight: 750;
      text-transform: none;
    }

    .fp-back-button.MuiButton-root:hover {
      background: #f5effb;
    }

    .fp-security-note {
      display: flex;
      align-items: flex-start;
      gap: 11px;
      margin-top: 27px;
      padding: 15px;
      border: 1px solid #eee6f6;
      border-radius: 13px;
      background: #faf7ff;
    }

    .fp-security-icon {
      flex-shrink: 0;
      margin-top: 2px;
      color: var(--fp-purple);
      font-size: 19px;
    }

    .fp-security-note p {
      margin: 0;
      color: #777184;
      font-size: 11px;
      line-height: 1.8;
    }

    .fp-security-note strong {
      color: var(--fp-deep);
    }

    .fp-form-footer {
      margin-top: 28px;
      padding-top: 19px;
      border-top: 1px solid #eee8f4;
      color: #948ba0;
      font-size: 10px;
      line-height: 1.8;
      text-align: center;
    }

    .fp-form-footer span {
      color: var(--fp-purple);
      font-weight: 800;
    }

    .fp-alert {
      margin-top: 18px;
      border-radius: 12px !important;
      font-size: 12px !important;
      overflow-wrap: anywhere;
    }

    @media (max-width: 800px) {
      .fp-layout {
        grid-template-columns: minmax(0, 1fr);
        max-width: 510px;
        min-height: auto;
      }

      .fp-brand-panel {
        padding: 27px 28px;
      }

      .fp-brand-middle {
        margin: 34px 0;
      }

      .fp-brand-heading {
        max-width: 400px;
        font-size: 34px;
      }

      .fp-brand-description {
        max-width: 430px;
      }

      .fp-brand-points {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
      }

      .fp-brand-bottom {
        margin-top: 5px;
      }

      .fp-form-panel {
        padding: 39px 28px;
      }

      .fp-form-content {
        max-width: 440px;
      }

      .fp-shape-three {
        right: 4%;
      }
    }

    @media (max-width: 450px) {
      .jana-forgot-page {
        padding: 15px 11px;
      }

      .fp-layout {
        border-radius: 21px;
      }

      .fp-brand-panel {
        padding: 24px 21px;
      }

      .fp-brand-heading {
        font-size: 30px;
      }

      .fp-brand-middle {
        margin: 29px 0;
      }

      .fp-brand-points {
        grid-template-columns: minmax(0, 1fr);
      }

      .fp-form-panel {
        padding: 32px 21px;
      }

      .fp-form-title {
        font-size: 29px;
      }

      .fp-form-icon {
        width: 60px;
        height: 60px;
        font-size: 29px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .fp-background-shape {
        animation: none !important;
      }

      .fp-primary-button.MuiButton-root {
        transition: none !important;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>

      <Box className="jana-forgot-page">
        <Box
          className="fp-background-shape fp-shape-one"
          aria-hidden="true"
        />
        <Box
          className="fp-background-shape fp-shape-two"
          aria-hidden="true"
        />
        <Box
          className="fp-background-shape fp-shape-three"
          aria-hidden="true"
        />
        <Box
          className="fp-background-shape fp-shape-four"
          aria-hidden="true"
        />

        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.99 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          style={{ width: "100%", maxWidth: 1030, zIndex: 1 }}
        >
          <Paper className="fp-layout" elevation={0}>
            {/* BRAND PANEL */}
            <Box className="fp-brand-panel">
              <Box className="fp-brand-top">
                <Box className="fp-brand-logo">
                  <SchoolRoundedIcon fontSize="inherit" />
                </Box>

                <Box>
                  <Typography className="fp-brand-name">
                    School Portal
                  </Typography>

                  <Typography className="fp-brand-caption">
                    Learning • Growth • Excellence
                  </Typography>
                </Box>
              </Box>

              <Box className="fp-brand-middle">
                <Typography className="fp-brand-eyebrow">
                  Account Recovery
                </Typography>

                <Typography component="h2" className="fp-brand-heading">
                  A secure way
                  <br />
                  back to <span>learning.</span>
                </Typography>

                <Typography className="fp-brand-description">
                  Forgot your password? No worries. Follow a few simple
                  steps to securely recover your school account and
                  continue your educational journey.
                </Typography>

                <Box className="fp-brand-points">
                  <Box className="fp-brand-point">
                    <Box className="fp-brand-point-icon">
                      <ShieldOutlinedIcon fontSize="inherit" />
                    </Box>
                    Secure verification
                  </Box>

                  <Box className="fp-brand-point">
                    <Box className="fp-brand-point-icon">
                      <EmailOutlinedIcon fontSize="inherit" />
                    </Box>
                    Email verification
                  </Box>

                  <Box className="fp-brand-point">
                    <Box className="fp-brand-point-icon">
                      <LockResetRoundedIcon fontSize="inherit" />
                    </Box>
                    Password recovery
                  </Box>

                  <Box className="fp-brand-point">
                    <Box className="fp-brand-point-icon">
                      <AutoAwesomeRoundedIcon fontSize="inherit" />
                    </Box>
                    Simple and convenient
                  </Box>
                </Box>
              </Box>

              <Box className="fp-brand-bottom">
                Your account security matters. Use the email address
                associated with your school account to begin recovery.
              </Box>
            </Box>

            {/* FORM PANEL */}
            <Box className="fp-form-panel">
              <Box className="fp-form-content">
                <Box className="fp-form-icon">
                  <LockResetRoundedIcon fontSize="inherit" />
                </Box>

                <Typography className="fp-form-eyebrow">
                  Password Assistance
                </Typography>

                <Typography component="h1" className="fp-form-title">
                  Forgot Password?
                </Typography>

                <Typography className="fp-form-description">
                  Enter your registered email address below. If your
                  account is eligible, we will send you a verification
                  code to continue resetting your password.
                </Typography>

                {message && (
                  <Alert
                    severity={messageType}
                    className="fp-alert"
                    onClose={() => setMessage("")}
                  >
                    {message}
                  </Alert>
                )}

                <Box
                  component="form"
                  onSubmit={handleRequest}
                  noValidate
                  sx={{ mt: 1 }}
                >
                  <TextField
                    className="fp-email-field"
                    fullWidth
                    required
                    type="email"
                    label="Registered Email Address"
                    placeholder="you@example.com"
                    autoComplete="email"
                    value={email}
                    disabled={loading}
                    onChange={(e) => setEmail(e.target.value)}
                    inputProps={{
                      maxLength: 254,
                      "aria-label": "Registered email address",
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon
                            sx={{ color: "#54218f", fontSize: 21 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    className="fp-primary-button"
                    fullWidth
                    type="submit"
                    disabled={loading || !email.trim()}
                    endIcon={
                      !loading ? (
                        <ArrowForwardRoundedIcon />
                      ) : undefined
                    }
                    sx={{ mt: 2.5 }}
                  >
                    {loading ? (
                      <>
                        <CircularProgress
                          size={20}
                          sx={{ color: "#fff", mr: 1.5 }}
                        />
                        Sending Verification Code...
                      </>
                    ) : (
                      "Send Verification Code"
                    )}
                  </Button>

                  <Button
                    className="fp-back-button"
                    fullWidth
                    type="button"
                    startIcon={<ArrowBackRoundedIcon />}
                    disabled={loading}
                    onClick={() => navigate("/login")}
                  >
                    Back to Login
                  </Button>
                </Box>

                <Box className="fp-security-note">
                  <ShieldOutlinedIcon className="fp-security-icon" />

                  <Typography component="p">
                    <strong>Security tip</strong>
                    <br />
                    Never share your verification code with anyone.
                    Only enter it on the official school portal.
                  </Typography>
                </Box>

                <Box className="fp-form-footer">
                  Secure Account Recovery
                  <br />
                  <span>School Portal</span>
                  {" "}• Designed for a better learning experience
                </Box>
              </Box>
            </Box>
          </Paper>
        </motion.div>
      </Box>
    </>
  );
};

export default ForgotPassword;
